import posthog from '../posthogConfig';
import { IEVENT_NAMES } from './eventNames';
import {
  IDefaultProperties,
  IInvoiceStatusChangedProperties,
  ILineItemAddedProperties,
  ILineItemRemovedProperties,
  IListFilterAppliedProperties,
  IListInvoiceCreateInitiatedProperties,
  IListItemSelectedProperties,
  IListNotesActionClickedProperties,
  IListPageSizeChangedProperties,
  IListPatientActionClickedProperties,
  IListSearchPerformedProperties,
  IListSortChangedProperties,
  IListTerminalPaymentInitiatedProperties,
  IPaymentCompletedProperties,
  IPaymentFailedProperties,
  ISendInvoiceLinkProperties,
  IInvoiceCreatedEventProperties,
} from './interfaces';
import { IActionType, IPaymentType } from './enums';
import { checkIfInternalUser } from 'Shared/Utils';

const getUserRole = () => {
  const userDetailStr = window.localStorage.getItem('userDetail');
  let role = 'user';

  if (userDetailStr) {
    try {
      const userDetail = JSON.parse(userDetailStr);
      const customRole = userDetail['custom:role'];
      if (customRole) {
        role = customRole.replace('r:', '');
      }
    } catch (error) {
      console.error('Failed to parse userDetail from localStorage:', error);
    }
  }

  return role;
};

const isinternaluser = () => {
  const userDetailStr = window.localStorage.getItem('userDetail');
  const user = JSON.parse(userDetailStr);
  return checkIfInternalUser(user);
};

export const mapActionKeyToType = (key: string): IActionType => {
  switch (key) {
    case 'copy_link':
      return IActionType.CopyLink;
    case 'edit_invoice':
      return IActionType.EditInvoice;
    case 'send_reminder':
      return IActionType.SendReminder;
    case 'make_payment':
      return IActionType.MakePayment;
    case 'cancel_invoice':
      return IActionType.CancelInvoice;
    case 'delete_invoice':
      return IActionType.DeleteInvoice;
    case 'view_receipt':
      return IActionType.ViewReceipt;
    case 'refund_payment':
      return IActionType.RefundPayment;
    case 'send_receipt':
      return IActionType.SendReceipt;
    default:
      return IActionType.EditInvoice;
  }
};

export const mapPaymentValuetoType = (key: string): IPaymentType => {
  switch (key) {
    case 'terminal':
      return IPaymentType.Terminal;
    case 'hsa_fsa':
      return IPaymentType.HsaFsa;
    case 'card':
      return IPaymentType.Card;
    case 'cash':
      return IPaymentType.Cash;
    default:
      return IPaymentType.Terminal;
  }
};

// Generic trackEvent function with type safety
export const trackEvent = (
  eventName: keyof typeof IEVENT_NAMES,
  properties: Record<string, any>,
) => {
  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const defaultProperties: IDefaultProperties = {
    origin_page:
      pathSegments.length > 1
        ? pathSegments.slice(-2).join('/')
        : pathSegments.pop() || 'unknown',
    url: window.location.href,
    referer: document.referrer,
    timestamp: new Date().toISOString(),
    user_role: getUserRole(),
    is_internal_user: isinternaluser(),
  };

  const eventData = {
    ...defaultProperties,
    ...properties,
  };
  posthog.capture(eventName, eventData);
};

// Helper functions with type safety

export const trackInvoiceStatusChanged = (
  properties: IInvoiceStatusChangedProperties,
) => {
  trackEvent(IEVENT_NAMES.INVOICE_STATUS_CHANGED, properties);
};

export const trackPaymentCompleted = (
  properties: IPaymentCompletedProperties,
) => {
  trackEvent(IEVENT_NAMES.PAYMENT_COMPLETED, properties);
};

export const trackPaymentFailed = (properties: IPaymentFailedProperties) => {
  trackEvent(IEVENT_NAMES.PAYMENT_FAILED, properties);
};

export const trackLineItemAdded = (properties: ILineItemAddedProperties) => {
  trackEvent(IEVENT_NAMES.LINE_ITEM_ADDED, properties);
};

export const trackLineItemRemoved = (
  properties: ILineItemRemovedProperties,
) => {
  trackEvent(IEVENT_NAMES.LINE_ITEM_REMOVED, properties);
};

export const trackListInvoiceCreateInitiated = (
  properties: IListInvoiceCreateInitiatedProperties,
) => {
  trackEvent(IEVENT_NAMES.LIST_INVOICE_CREATE_INITIATED, properties);
};

export const trackListPatientActionClicked = (
  properties: IListPatientActionClickedProperties,
) => {
  trackEvent(IEVENT_NAMES.LIST_PATIENT_ACTION_CLICKED, properties);
};

export const trackListFilterApplied = (
  properties: IListFilterAppliedProperties,
) => {
  trackEvent(IEVENT_NAMES.LIST_FILTER_APPLIED, properties);
};

export const trackListSortChanged = (
  properties: IListSortChangedProperties,
) => {
  trackEvent(IEVENT_NAMES.LIST_SORT_CHANGED, properties);
};

export const trackListPageSizeChanged = (
  properties: IListPageSizeChangedProperties,
) => {
  trackEvent(IEVENT_NAMES.LIST_PAGE_SIZE_CHANGED, properties);
};

export const trackListSearchPerformed = (
  properties: IListSearchPerformedProperties,
) => {
  trackEvent(IEVENT_NAMES.LIST_SEARCH_PERFORMED, properties);
};

export const trackListItemSelected = (
  properties: IListItemSelectedProperties,
) => {
  trackEvent(IEVENT_NAMES.LIST_ITEM_SELECTED, properties);
};

export const trackListNotesActionClicked = (
  properties: IListNotesActionClickedProperties,
) => {
  trackEvent(IEVENT_NAMES.LIST_NOTES_ACTION_CLICKED, properties);
};

export const trackListTerminalPaymentInitiated = (
  properties: IListTerminalPaymentInitiatedProperties,
) => {
  trackEvent(IEVENT_NAMES.LIST_TERMINAL_PAYMENT_INITIATED, properties);
};

export const trackSendInvoiceEvent = (
  properties: ISendInvoiceLinkProperties,
) => {
  trackEvent(IEVENT_NAMES.SEND_INVOICE_LINK, properties);
};

export const trackInvoiceCreatedEvent = (
  properties: IInvoiceCreatedEventProperties,
) => {
  trackEvent(IEVENT_NAMES.INVOICE_CREATED, properties);
};

export const trackInvoiceEvents = (
  eventName: keyof typeof IEVENT_NAMES,
  properties: Record<string, any>,
) => {
  trackEvent(eventName, properties);
};
